import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import './AuthPages.css';
import LoadingOverlay from './LoadingOverlay';
import config from '../config';

function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  const API_BASE_URL = config.backendUrl;

  // Handle redirectUrl when component mounts
  useEffect(() => {
    // Reset authentication state when component mounts
    setIsAuthenticating(false);
    
    // Clear any persisted authentication state
    localStorage.removeItem('isAuthenticating');
    
    // Check if we need to log out the user
    const action = searchParams.get('action');
    
    if (action === 'logout') {
      console.log('Logout action detected, signing out user');
      
      // Sign out the user
      signOut(auth).then(() => {
        console.log('User signed out successfully');
        // Clear any Unity auth data from localStorage
        localStorage.removeItem('unityAuthData');
        // Clear any redirect URLs
        localStorage.removeItem('redirectUrl');
        // Set success message
        setSuccess('You have been successfully logged out.');
        
        // Remove the action parameter from the URL to prevent repeated logouts on refresh
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('action');
        window.history.replaceState({}, document.title, newUrl.toString());
      }).catch((error) => {
        console.error('Error signing out:', error);
        setError('Failed to sign out. Please try again.');
      });
    }
    
    // Try different methods to get the redirect URL
    let effectiveRedirectUrl = null;
    
    // Method 1: From location.search via searchParams
    if (redirectUrl) {
      effectiveRedirectUrl = redirectUrl;
    }
    
    // Method 2: Parse the URL directly
    if (!effectiveRedirectUrl) {
      const fullUrl = window.location.href;
      
      // Check if the URL contains a redirect parameter
      const redirectIndex = fullUrl.indexOf('redirect=');
      if (redirectIndex !== -1) {
        // Extract everything after 'redirect='
        const redirectPart = fullUrl.substring(redirectIndex + 9);
        // If there are other parameters, only take up to the &
        const endIndex = redirectPart.indexOf('&');
        effectiveRedirectUrl = endIndex !== -1 ? 
          redirectPart.substring(0, endIndex) : 
          redirectPart;
          
        // Handle URL encoding if needed
        try {
          effectiveRedirectUrl = decodeURIComponent(effectiveRedirectUrl);
        } catch (e) {
          console.error('Error decoding URL:', e);
        }
      }
    }
    
    // Method 3: Check if the redirect is part of the URL path
    if (!effectiveRedirectUrl) {
      const pathParts = window.location.pathname.split('/');
      for (let i = 0; i < pathParts.length; i++) {
        if (pathParts[i] === 'redirect' && i + 1 < pathParts.length) {
          effectiveRedirectUrl = pathParts[i + 1];
          break;
        }
      }
    }
    
    // Method 4: Check for a hash parameter
    if (!effectiveRedirectUrl && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hashRedirect = hashParams.get('redirect');
      if (hashRedirect) {
        effectiveRedirectUrl = hashRedirect;
      }
    }
    
    // If we found a redirect URL by any method, store it
    if (effectiveRedirectUrl) {
      localStorage.setItem('redirectUrl', effectiveRedirectUrl);
      console.log('Stored redirect URL in localStorage:', effectiveRedirectUrl);
    } else {
      console.log('No redirect URL found in params');
      // Don't remove from localStorage on initial load
      // This allows the redirect to persist through navigation
    }
  }, [location, redirectUrl]);

  // State for email sign-in
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(localStorage.getItem('isAuthenticating') === 'true');

  // Function to close the loading overlay
  const handleCloseLoadingOverlay = () => {
    setIsAuthenticating(false);
    localStorage.removeItem('isAuthenticating');
  };

  const handleGoogleAuth = async () => {
    try {
      setLoading(true);
      setError('');
      setIsAuthenticating(true);
      // Store authentication state in localStorage to track across page refreshes
      localStorage.setItem('isAuthenticating', 'true');
      
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // The auth state listener in App.jsx will handle Unity authentication
      // No need to call /auth/unity endpoint here
      
      // Don't hide the loading overlay after successful authentication
      // It will remain visible during account setup and redirect

    } catch (error) {
      console.error('Google authentication error:', error);
      // Provide user-friendly error message instead of raw Firebase error
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Pop-up blocked by browser. Please allow pop-ups for this site');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else {
        setError('Failed to sign in with Google. Please try again');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      }
    } finally {
      setLoading(false);
      // Note: We don't set isAuthenticating to false on success
      // because we want the loading overlay to remain visible
    }
  };

  const handleAppleAuth = async () => {
    try {
      setLoading(true);
      setError('');
      setIsAuthenticating(true);
      // Store authentication state in localStorage to track across page refreshes
      localStorage.setItem('isAuthenticating', 'true');
      
      const provider = new OAuthProvider('apple.com');
      const result = await signInWithPopup(auth, provider);
    
      // The auth state listener in App.jsx will handle Unity authentication
      // No need to call /auth/unity endpoint here
      
      // Don't hide the loading overlay after successful authentication
      // It will remain visible during account setup and redirect

    } catch (error) {
      console.error('Apple authentication error:', error);
      // Provide user-friendly error message instead of raw Firebase error
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Pop-up blocked by browser. Please allow pop-ups for this site');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      } else {
        setError('Failed to sign in with Apple. Please try again');
        setIsAuthenticating(false);
        localStorage.removeItem('isAuthenticating');
      }
    } finally {
      setLoading(false);
      // Note: We don't set isAuthenticating to false on success
      // because we want the loading overlay to remain visible
    }
  };
  
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setIsAuthenticating(true);
      // Store authentication state in localStorage to track across page refreshes
      localStorage.setItem('isAuthenticating', 'true');
      
      let userCredential;
      
      if (isSignUp) {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      }
      
      // The auth state listener in App.jsx will handle Unity authentication
      // No need to call /auth/unity endpoint here
      
      // Don't hide the loading overlay after successful authentication
      // It will remain visible during account setup and redirect
      
    } catch (error) {
      console.error('Email authentication error:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format');
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setError('Incorrect email or password');
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many unsuccessful login attempts. Please try again later');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again');
      } else {
        // Generic error message instead of showing raw Firebase errors
        setError(isSignUp ? 'Failed to create account. Please try again' : 'Failed to sign in. Please check your credentials');
      }
      setIsAuthenticating(false);
      localStorage.removeItem('isAuthenticating');
    } finally {
      setLoading(false);
      // Note: We don't set isAuthenticating to false on success
      // because we want the loading overlay to remain visible
    }
  };
  
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setIsAuthenticating(true);
      
      await sendPasswordResetEmail(auth, resetEmail);
      
      setResetSent(true);
      setSuccess('Password reset email sent! Check your inbox for instructions.');
      setResetEmail('');
    } catch (error) {
      console.error('Password reset error:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format');
      } else if (error.code === 'auth/user-not-found') {
        setError('If an account exists with this email, a password reset link will be sent');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again');
      } else {
        // Generic error message instead of showing raw Firebase errors
        setError('Unable to send password reset email. Please try again later');
      }
      setIsAuthenticating(false);
      localStorage.removeItem('isAuthenticating');
    } finally {
      setLoading(false);
      setIsAuthenticating(false);
      localStorage.removeItem('isAuthenticating');
    }
  };
  
  const toggleAuthMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setSuccess('');
  };
  
  const showPasswordResetForm = () => {
    setShowResetForm(true);
    setShowEmailForm(false);
    setError('');
    setSuccess('');
  };
  
  const backToSignIn = () => {
    setShowResetForm(false);
    setShowEmailForm(true);
    setResetSent(false);
    setError('');
    setSuccess('');
  };

  // Clear authentication state on component unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem('isAuthenticating');
    };
  }, []);

  return (
    <div className="auth-container">
      {isAuthenticating && <LoadingOverlay message="Setting up your account and preparing for redirect. This may take a moment..." onClose={handleCloseLoadingOverlay} />}
      <div className="auth-logo">
        <img src="/images/dream-book-logo-dark.png" alt="Dreambook Logo" />
      </div>
      
      <div className="auth-welcome">
        <h2>Welcome to dream book 3D</h2>
        <p className="auth-description">
          {showResetForm 
            ? 'Reset your password' 
            : isSignUp 
              ? 'Create your dream book 3D account' 
              : 'Sign in to your dream book 3D account'
          }
        </p>
      </div>
      
      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}
      
      {showResetForm ? (
        <div className="email-auth-form">
          {resetSent ? (
            <div className="reset-success-container">
              <div className="reset-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3>Check your email</h3>
              <p>We've sent instructions to reset your password. Please check your inbox and follow the link to create a new password.</p>
            </div>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <div className="form-group">
                <label className="form-label" htmlFor="reset-email">Email address</label>
                <input
                  type="email"
                  id="reset-email"
                  placeholder="Enter your email address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="auth-input"
                  required
                  aria-label="Email address for password reset"
                />
              </div>
              <button 
                type="submit" 
                className="auth-button email-button"
                disabled={loading}
              >
                {loading ? (
                  <span className="button-loading">
                    <svg className="spinner" viewBox="0 0 50 50">
                      <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                    </svg>
                    Please wait...
                  </span>
                ) : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      ) : showEmailForm ? (
        <div className="email-auth-form">
          <form onSubmit={handleEmailAuth}>
            <div className="form-group">
              <label className="form-label" htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                required
                aria-label="Email address"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                required
                aria-label="Password"
                minLength={6}
              />
            </div>
            <button 
              type="submit" 
              className="auth-button email-button"
              disabled={loading}
            >
              {loading ? (
                <span className="button-loading">
                  <svg className="spinner" viewBox="0 0 50 50">
                    <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                  </svg>
                  Please wait...
                </span>
              ) : isSignUp ? 'Create Account' : 'Sign In'}
            </button>
          </form>
          
          {!isSignUp && (
            <div className="forgot-password">
              <button 
                onClick={showPasswordResetForm}
                className="text-button"
                tabIndex="0"
                aria-label="Forgot password"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    showPasswordResetForm();
                  }
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          
          <div className="auth-switch">
            <p>{isSignUp ? 'Already have an account?' : 'Need an account?'}</p>
            <button 
              onClick={toggleAuthMode}
              className="auth-switch-button"
              tabIndex="0"
              aria-label={isSignUp ? 'Sign in to existing account' : 'Create a new account'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  toggleAuthMode();
                }
              }}
            >
              {isSignUp ? 'Sign In' : 'Create Account'}
            </button>
          </div>
          
          <div className="auth-divider">
            <span>or</span>
          </div>
          
          <button 
            className="auth-button secondary"
            onClick={() => setShowEmailForm(false)}
            tabIndex="0"
            aria-label="Go back to sign-in options"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setShowEmailForm(false);
              }
            }}
          >
            Back to Sign-In Options
          </button>
        </div>
      ) : (
        <>
          <div className="auth-buttons">
            <button 
              className="auth-button google-button" 
              onClick={handleGoogleAuth}
              disabled={loading}
              tabIndex="0"
              aria-label="Continue with Google"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleGoogleAuth();
                }
              }}
            >
              <img src="/icons/google.svg" alt="" />
              Continue with Google
            </button>
            <button 
              className="auth-button apple-button" 
              onClick={handleAppleAuth}
              disabled={loading}
              tabIndex="0"
              aria-label="Continue with Apple"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleAppleAuth();
                }
              }}
            >
              <img src="/icons/apple.svg" alt="" style={{filter: 'invert(1)'}} />
              Continue with Apple
            </button>
            <button 
              className="auth-button email-button" 
              onClick={() => setShowEmailForm(true)}
              disabled={loading}
              tabIndex="0"
              aria-label="Continue with Email"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setShowEmailForm(true);
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-10 7-10-7" />
              </svg>
              Continue with Email
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default AuthPage;